import { useSelector } from "react-redux";
import { routes } from "../../app/routes";
import { TenantModel } from "../../data/entities";
import {useCRUDHandlers, useGridActions, 
    usePermission, 
    useSetPageTitle,
    useSharedState,
} from "../../hooks";
import { TenantService } from "../../services";
import { getUserTenantId } from "../../app";
import { Breakpoints, Button, ConfirmationModal, Icon, IconAndText, IconDefinitions, ODataGrid, ResponsiveGridColumn, TitlelessCard } from "../../components";
import { GridCellProps, MenuItem } from "@progress/kendo-react-all";
import { PermissionDefinitions } from "../../security";
import { usePageActions } from "../PageLayout";
import { useCallback } from "react";
import { AddTenantModal } from "./components";
import { createODataQueryFilter } from "../../utils";


export function HostTenantsPage() {  
    const gridData = useSharedState<Array<TenantModel>>([]);
    const tenantService = new TenantService();
    const { onClick, onSelect, showAddState, showDelete, showDeleteState, deleteItemState} = useGridActions<TenantModel>(routes.host.tenant.details);
    const { gridRef, getItems, addItem, deleteItem } = useCRUDHandlers<TenantModel>(tenantService, "Tenant", showAddState);
    const tenantId = useSelector(getUserTenantId());
    const canAddTenant = usePermission(PermissionDefinitions.host.create());
    const canDeleteTenant = usePermission(PermissionDefinitions.host.delete(tenantId!));

    useSetPageTitle("Tenants", []);
    usePageActions(
        canAddTenant && (
          <Button
            onClick={() => {
              showAddState.setter(true);
            }}
          >
            <IconAndText iconName={IconDefinitions.add} text="Add" />
          </Button>
        )
        , [canAddTenant]);
    
      const commandsCell = useCallback(
        (props: GridCellProps) => (
          <>
          {canDeleteTenant && (
            <td className="text-center">
              <Icon
                iconName={IconDefinitions.delete}
                className="text-danger"
                onClick={() => showDelete(props.dataItem)}
              />
            </td>
          )}
          </>
        ),
        [showDelete, canDeleteTenant]
      );

    return (
     <TitlelessCard bodyClassName="p-0">
      <ODataGrid
        ref={gridRef}
        getData={(oDataQuery) => getItems(createODataQueryFilter(oDataQuery))}
        dataState={gridData}
        onRowClick={onClick}
        onRowMenuSelect={onSelect}
        sort={[{ field: "name", dir: "asc"}]}
        scrollable="scrollable"
        pageSize={10}
       >
        <ResponsiveGridColumn
            title="Name"
            field="name"
            sortable={true}
        />
        <ResponsiveGridColumn
            className="column-url"
            field="url"
            title=" "
            sortable={false}
            filterable={false}
            headerCell={() => null}
            cell={canDeleteTenant === true ? commandsCell : undefined}
            width={50}
            breakpoint={Breakpoints.sm}
        />
        <MenuItem
            text="Open"
            data={{ action: "open" }}
            icon="hyperlink-open"
        />
        <MenuItem
          text="Open Tab"
          data={{ action: "opentab" }}
          icon="windows"
        />
        {
          canDeleteTenant && (
            <MenuItem
              text="Delete"
              data={{ action: "delete" }}
              icon={IconDefinitions.delete}
            />
          )
        }
      </ODataGrid>

      <AddTenantModal
        showAddState={showAddState}
        showState={showAddState}
        onSubmit={addItem}
      />
      
      <ConfirmationModal
        showState={showDeleteState}
        title={`Delete ${deleteItemState.value?.name}`}
        message={`You are about to delete this tenant, it cannot be undone. Are you sure you want to delete ${deleteItemState.value.name}?`}
        confirmText="Delete"
        onConfirm={() => deleteItem(deleteItemState.value)}
      />
     </TitlelessCard>
    );
}

export default HostTenantsPage;